'use client'

import { type IChatMinData, useSession } from '@repo/sdk'
import { KitAvatar, Separator } from '@repo/ui'
import { useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'

const LocationDrawer = <T extends IChatMinData>({ data }: { data: T }) => {
  const toRef = useRef<HTMLDivElement>(null)
  const [lineBottom, setLineBottom] = useState<string>('0px')

  useEffect(() => {
    if (toRef.current) {
      const toRect = toRef.current.getBoundingClientRect()

      setLineBottom(`${toRect.height - 7}px`)
    }
  }, [data, toRef])

  return (
    <div className={'relative flex flex-col gap-x-4'}>
      <div className="flex items-center gap-2">
        <div className="z-50 mt-1.5 h-2 w-2 rounded-full bg-black" />
        <p>{data?.origin.place_name}</p>
      </div>

      <div
        className="absolute"
        style={{
          backgroundColor: '#0003',
          bottom: lineBottom,
          left: '3px',
          top: '10px',
          width: '2px',
        }}
      />

      <div className="flex items-center gap-2" ref={toRef}>
        <div className="z-50 mt-1.5 h-2 w-2 bg-black" />
        <p className="font-normal">{data?.destination.place_name}</p>
      </div>
    </div>
  )
}

interface IProps<T extends IChatMinData> {
  data?: T
  hideLoadId?: boolean
  hideLocation?: boolean
}

export const ChatHeader = <T extends IChatMinData>({
  data,
  hideLoadId = false,
  hideLocation = false,
}: IProps<T>) => {
  const { data: session } = useSession()

  const userId = z.coerce.number().parse(session?.user?.id)

  const chatMembers = data?.chat?.members?.filter(
    member => member.id !== userId && member.name !== 'System',
  )

  const chatMemberFullName = useMemo(() => {
    let name: string | undefined = undefined

    if (chatMembers?.[0]) {
      if (chatMembers[0].name) {
        name = chatMembers[0].name
      }

      if (chatMembers[0].last_name) {
        name += ` ${chatMembers[0].last_name}`
      }
    }

    return name
  }, [chatMembers])

  return (
    <div className={'flex w-full flex-col items-center gap-2'}>
      {!hideLoadId && (
        <>
          <p className={'mt-2 text-lg'}>Chat</p>
          <Separator />
        </>
      )}
      <div className={'flex w-full items-center justify-between gap-4 px-4'}>
        <div className={'flex items-center gap-2'}>
          <KitAvatar />
          <p>{chatMemberFullName ?? 'Admin'}</p>
        </div>
        {data && !hideLocation && <LocationDrawer data={data} />}
      </div>
      <Separator />
    </div>
  )
}
